import { SEOContentFAQ } from "../../../../components/seo/SEOContentFAQ";
import { SEOContentHowTo } from "../../../../components/seo/SEOContentHowTo";

export const TeleprompterGoogleMeetTeamsEN = () => (
    <>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Teleprompter for Google Meet and Teams: Master Your Professional Meetings
        </h2>

        <p className="lead text-xl text-slate-300 mb-8 font-medium">
            Projecting confidence and authority in virtual meetings is the new executive edge. <strong>Discreetly read your scripts and notes</strong> while maintaining perfect eye contact.
        </p>

        <p className="text-slate-300 mb-8 leading-relaxed">
            In this essential guide for modern professionals, we show how to transform your Google Meet or Microsoft Teams into a true corporate stage with PromptNinja. Learn the invisible overlay technique to keep your eyes fixed on the lens, ensuring you never lose your train of thought or forget a crucial point in your presentation. Discover how our discreet online tool allows you to present with the confidence of an experienced leader, conveying executive presence and total subject mastery, without your audience knowing you're using a teleprompter.
        </p>

        <div className="bg-blue-600/10 border border-blue-500/20 rounded-2xl p-6 md:p-8 text-center mb-12 shadow-xl">
            <h3 className="text-2xl font-bold text-white mb-4">Keep Focus on What Matters</h3>
            <p className="text-slate-300 mb-6 font-medium">
                Speak directly into the lens and convey authority effortlessly.
                <strong>PromptNinja</strong> is invisible to your audience and indispensable to you.
            </p>
            <a href="/?lang=en#app" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full transition-all hover:scale-105 shadow-lg shadow-blue-500/25">
                Open Discreet Teleprompter
            </a>
            <p className="mt-4 text-sm text-slate-400 font-medium">Maintain executive presence instantly.</p>
        </div>

        <div className="bg-gradient-to-r from-red-900/30 to-green-900/30 p-8 rounded-xl border border-slate-700 mb-12">
            <h3 className="text-3xl font-bold text-white mb-6">The Attention Battle: Notes vs Teleprompter</h3>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-900/70 p-6 rounded-lg border border-red-500/30">
                    <h3 className="font-bold text-red-400 mb-4 flex items-center gap-2">
                        <span>❌</span> The "Note Reader"
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>👀 <strong>Gaze:</strong> Darts down/sideways every 10s</li>
                        <li>📉 <strong>Perception:</strong> "He's reading", "Doesn't know the topic"</li>
                        <li>🤝 <strong>Connection:</strong> Constantly broken</li>
                        <li>😰 <strong>Anxiety:</strong> High (fear of getting lost in paper)</li>
                        <li>📺 <strong>Result:</strong> Boring meeting, low conversion</li>
                    </ul>
                </div>
                <div className="bg-slate-900/70 p-6 rounded-lg border border-green-500/30">
                    <h3 className="font-bold text-green-400 mb-4 flex items-center gap-2">
                        <span>✅</span> The "Connected Leader" (PromptNinja)
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>👀 <strong>Gaze:</strong> 100% into the webcam lens</li>
                        <li>📈 <strong>Perception:</strong> Authoritative, confident, present</li>
                        <li>🤝 <strong>Connection:</strong> Intense (virtual eye contact)</li>
                        <li>😌 <strong>Anxiety:</strong> Zero (text is right there, floating)</li>
                        <li>📺 <strong>Result:</strong> Total engagement, high persuasion</li>
                    </ul>
                </div>
            </div>
            <p className="text-slate-400 text-sm mt-6 italic text-center">
                💡 Virtual Eye Contact = Power. Whoever dominates the lens, dominates the meeting.
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">3 Setups for Online Meetings</h3>
        <div className="space-y-6 mb-12">
            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-indigo-500">
                <h3 className="text-xl font-bold text-indigo-400 mb-3">Setup #1: Single Monitor (Laptop) - "The Invisible Overlay"</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> Just your laptop with built-in webcam. Ideal for travel or simple home office.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li>Open Google Meet/Teams and maximize the window.</li>
                    <li>Open PromptNinja in a NEW browser window.</li>
                    <li>Resize PromptNinja to be <strong>narrow and transparent</strong>.</li>
                    <li>Position this window EXACTLY at the top-center, just below the physical webcam dot.</li>
                    <li><strong>The Trick:</strong> You read text that is 1 inch from the camera. To viewers, it looks like you are looking into their eyes.</li>
                </ol>
                <p className="text-green-400 text-sm mt-3">✅ <strong>Ideal for:</strong> Daily scrums, quick check-ins, digital nomads.</p>
            </div>

            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-blue-500">
                <h3 className="text-xl font-bold text-blue-400 mb-3">Setup #2: Dual Monitor - "Command Center"</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> Laptop + External Monitor.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li><strong>Monitor 1 (with Webcam):</strong> This is where PromptNinja goes. Centered at the top.</li>
                    <li><strong>Monitor 2 (Side):</strong> This is where Teams/Meet participants' faces or your PowerPoint presentation go.</li>
                    <li><strong>Flow:</strong> You present looking at Monitor 1 (Camera + Text). Consult reactions on Monitor 2 quickly.</li>
                    <li>This avoids the classic error of looking at the side monitor (profile view) while talking to the front camera.</li>
                </ol>
                <p className="text-green-400 text-sm mt-3">✅ <strong>Ideal for:</strong> Complex sales, online classes, team management.</p>
            </div>

            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-purple-400 mb-3">Setup #3: Job Interview (Stealth Mode)</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Scenario:</strong> You're being interviewed and want to cheat sheet your "STAR" answers without looking memorized.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li>PromptNinja with 30% opacity (almost invisible).</li>
                    <li>Text in bullet points, not full sentences (to sound natural).</li>
                    <li>Meet window BEHIND teleprompter, so you see interviewer's reaction THROUGH the text.</li>
                    <li>Use very slow auto-scroll or manual control via keyboard/mouse to avoid looking robotic.</li>
                </ol>
                <p className="text-yellow-400 text-sm mt-3">⚠️ <strong>Pro tip:</strong> Don't read. Use as "keyword cheat sheet" to unlock your memory.</p>
            </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg my-8 border-l-4 border-red-500">
            <h3 className="text-2xl font-bold text-white mb-4">🚨 The Danger of Screen Sharing</h3>
            <p className="text-slate-300 mb-4">
                The #1 error that kills careers: sharing the wrong screen and showing your script (or your WhatsApp) to the client.
            </p>
            <SEOContentHowTo
                title=""
                schemaTitle="How to Share Screen Safely on Teams/Meet"
                totalTime="PT1M"
                tools={["Google Meet/Teams", "PromptNinja"]}
                steps={[
                    {
                        title: "Golden Rule: Never 'Entire Screen'",
                        text: "Never use the 'Share Entire Screen' option. This shows everything, including your overlaid PromptNinja."
                    },
                    {
                        title: "Use 'Share Window'",
                        text: "In Meet/Teams, choose 'A Window'. Select ONLY the PowerPoint or PDF you want to show."
                    },
                    {
                        title: "The Trick",
                        text: "Since you shared only the PPT window, PromptNinja can physically sit IN FRONT of the PPT on your screen. YOU see the teleprompter over the slide. THE CLIENT sees only the clean slide."
                    }
                ]}
            />
            <p className="text-slate-300 mt-4 text-sm">
                This lets you have "Augmented Reality Subtitles" over your own presentation, invisible to the audience.
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">5 Mistakes That Reveal You're Reading</h3>
        <div className="space-y-4 mb-12">
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #1: Ping-Pong Eyes (Fast Reading)</h4>
                <p className="text-slate-300 text-sm">
                    Your eyes move left to right rapidly. On big screens, this is obvious.
                    <span className="text-green-400 block mt-2">✅ <strong>Solution:</strong> Reduce PromptNinja window WIDTH to 300px-400px. The narrower the text, the less your eyes move laterally.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #2: Monitor Glare in Glasses</h4>
                <p className="text-slate-300 text-sm">
                    White generic Word background reflects in your lenses. Text scrolling is visible in reflection.
                    <span className="text-green-400 block mt-2">✅ <strong>Solution:</strong> PromptNinja has BLACK background by default. This cuts 90% of reflection. Adjust monitor brightness to comfortable minimum.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #3: Forgetting to Blink and Smile</h4>
                <p className="text-slate-300 text-sm">
                    The "Reading Face": wide eyes, rarely blinking, serious face. Scary in HD.
                    <span className="text-green-400 block mt-2">✅ <strong>Solution:</strong> Put emojis 😄 or reminders [SMILE] and [BREATHE] in middle of text. They break robotic patterns.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #4: Window Far from Camera</h4>
                <p className="text-slate-300 text-sm">
                    Teleprompter at bottom of screen. You look like you're staring at their chin or desk.
                    <span className="text-green-400 block mt-2">✅ <strong>Solution:</strong> Text must be at the "Summit" of your screen, as close as possible to the physical top edge.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #5: Monotone Voice</h4>
                <p className="text-slate-300 text-sm">
                    Reading long sentences without natural pauses. Sounds like voice synthesis.
                    <span className="text-green-400 block mt-2">✅ <strong>Solution:</strong> Write like you speak, not like an email. Use short sentences. Use CAPS LOCK for words needing vocal emphasis.</span>
                </p>
            </div>
        </div>

        <div className="text-center bg-slate-800 p-8 rounded-lg my-10">
            <h3 className="text-3xl font-bold text-white mb-4">💼 Professionalize Your Home Office</h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Don't let tech get in the way of connection. Use it to amplify your presence.
            </p>
            <a
                href="/?lang=en#app"
                className="inline-block bg-gradient-to-r from-indigo-500 to-blue-600 font-bold py-3 px-8 rounded-full transition hover:brightness-110"
                style={{ color: 'white' }}
            >
                🚀 Prepare Meeting Now (Free)
            </a>
            <p className="text-slate-400 mt-4 text-sm">Works on Chrome, Edge, Safari • Safe and Private</p>
        </div>

        <SEOContentFAQ
            title="Frequently Asked Questions - Online Meetings"
            items={[
                {
                    question: "Does Microsoft Teams or Google Meet detect I'm using another window?",
                    answer: "Not the content. They know you don't have their window 'focused' (active), but don't know WHAT you are seeing. To them, you could be looking at notes. PromptNinja is invisible to the call API."
                },
                {
                    question: "How to switch slides and scroll text at the same time?",
                    answer: "Best not to do both. Option A: Auto-text (Play with low speed). Option B: Use Mouse Scroll for text and Keyboard (Arrows) for slides. Keep mouse focus on PromptNinja window."
                },
                {
                    question: "Works with blurred background or virtual background?",
                    answer: "Yes, that's processed by camera/meeting app. PromptNinja is just a window on your screen, doesn't interfere with your webcam video feed. It helps you LOOK at the webcam."
                },
                {
                    question: "What if internet drops?",
                    answer: "PromptNinja is PWA and works Offline. If internet drops mid-meeting, Meet/Teams freezes, but your script stays there, safe, for when you reconnect via 4G."
                },
                {
                    question: "Can I use this for job interviews?",
                    answer: "Yes, it's a common 'secret weapon'. Key is not writing full speech. Write keywords: 'PROJECT X - RESULT 20% - AGILE LEADERSHIP'. Look at words and speak naturally. If you read full sentences, trained HR will notice."
                },
                {
                    question: "Do I need an ultrawide monitor?",
                    answer: "No, actually very wide monitors can hinder if you put text in corner. Secret is always centering text just below camera, regardless of monitor size."
                },
                {
                    question: "Works on Zoom and Webex too?",
                    answer: "Yes, works over ANY video conferencing app. Principle is same: floating overlay window, close to camera, window sharing (not screen)."
                }
            ]}
        />
    </>
);
